import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../config';
import {
  ColumnMapping,
  MasterFilterKey,
  MasterSearchKey,
  MatchMode,
} from '../../../../../types/master';
import { buildLegacyMirror } from '../../../../../utils/master/matchKeys';

interface UpdateMasterMappingParams {
  masterId: string;
  mappingId: string;
  patternId?: string;
  patternName?: string;
  /**
   * 検索キー（#3180）。指定時は既存フィールド（search_item_id 等）も
   * ミラーとして同時に更新する。
   */
  searchKey?: MasterSearchKey;
  /** 絞り込みキー（#3180）。空配列を渡せば絞り込みなしに戻せる。 */
  filterKeys?: MasterFilterKey[];
  /** 自動反映の下限スコア（#3263）。0〜100の整数(%)。 */
  autoApplyScore?: number;
  /** 要注意にする下限スコア（#3309）。0〜100の整数(%)。 */
  reviewThreshold?: number;
  /** AI照合リランク（#3355）。false を渡せば OFF に戻せる。 */
  enableLlmRerank?: boolean;
  columnMappings?: ColumnMapping[];
  // 紐づけ単位（取引先コードなど）の OCR 項目。未指定は空文字で上書き保存。
  scopeItemId?: string;
  scopeItemLabel?: string;
  /** パターン内での実行順（#3180）。 */
  executionOrder?: number;
}

type UpdateValue =
  | string
  | number
  | boolean
  | ColumnMapping[]
  | MasterFilterKey[]
  | MasterSearchKey
  | Timestamp
  | MatchMode;

export const updateMasterMapping = async (params: UpdateMasterMappingParams): Promise<void> => {
  const mappingRef = doc(db, 'masters', params.masterId, 'master_mappings', params.mappingId);

  const updateData: Record<string, UpdateValue> = {
    updated_at: Timestamp.now(),
  };
  if (params.patternId !== undefined) updateData.pattern_id = params.patternId;
  if (params.patternName !== undefined) updateData.pattern_name = params.patternName;

  // 検索キーは新形式を正としつつ、既存フィールドにも同じ値を書き続ける（#3180）。
  // 片方だけ更新すると、旧フィールドを見ている画面・Cloud SQL の解決とずれる。
  if (params.searchKey !== undefined) {
    const mirror = buildLegacyMirror(params.searchKey);
    updateData.search_key = params.searchKey;
    updateData.search_item_id = mirror.searchItemId;
    updateData.search_item_label = mirror.searchItemLabel;
    updateData.search_csv_column = mirror.searchCsvColumn;
    updateData.match_mode = mirror.matchMode;
  }

  if (params.filterKeys !== undefined) updateData.filter_keys = params.filterKeys;
  if (params.autoApplyScore !== undefined) updateData.auto_apply_score = params.autoApplyScore;
  if (params.reviewThreshold !== undefined) updateData.review_threshold = params.reviewThreshold;
  if (params.enableLlmRerank !== undefined) updateData.enable_llm_rerank = params.enableLlmRerank;
  if (params.columnMappings !== undefined) updateData.column_mappings = params.columnMappings;
  if (params.scopeItemId !== undefined) updateData.scope_item_id = params.scopeItemId;
  if (params.scopeItemLabel !== undefined) updateData.scope_item_label = params.scopeItemLabel;
  if (params.executionOrder !== undefined) updateData.execution_order = params.executionOrder;

  await updateDoc(mappingRef, updateData);
};
